import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { supabase } from 'lib/supabase';

const EmailVerificationPending = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const fromClientRegistration = location?.state?.fromClientRegistration || false;
  const savedFormData = location?.state?.formData || null;

  useEffect(() => {
    // Get email from location state or redirect to login
    const userEmail = location?.state?.email;
    if (!userEmail) {
      navigate('/login');
      return;
    }
    setEmail(userEmail);
  }, [location, navigate]);

  useEffect(() => {
    // Countdown timer for resend button
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleResendVerification = async () => {
    if (countdown > 0) return;

    setIsResending(true);
    setResendError('');
    setResendSuccess(false);

    try {
      // Resend verification email via Supabase
      const { error } = await supabase?.auth?.resend({
        type: 'signup',
        email: email
      });

      if (error) {
        throw error;
      }

      setResendSuccess(true);
      setCountdown(60); // 60 second cooldown

      // Clear success message after 5 seconds
      setTimeout(() => {
        setResendSuccess(false);
      }, 5000);
    } catch (err) {
      console.error('Resend error:', err);
      setResendError(err?.message || 'Failed to resend verification email. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const handleProceedToMatching = () => {
    navigate('/lawyer-matching', { state: { formData: savedFormData } });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-full mb-4">
            <Icon name="Mail" size={32} className="text-white" />
          </div>
          <h1 className="text-3xl font-bold text-text-primary mb-2">Verify Your Email</h1>
          <p className="text-text-secondary">We've sent a verification link to your email</p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
          {/* Email Display */}
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <Icon name="Mail" size={20} className="text-primary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-text-secondary mb-1">Verification email sent to:</p>
                <p className="text-base font-medium text-text-primary truncate">{email}</p>
              </div>
            </div>
          </div>

          {/* Instructions */}
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center mt-0.5">
                <span className="text-xs font-bold text-primary">1</span>
              </div>
              <div>
                <p className="text-sm text-text-primary font-medium">Check your inbox</p>
                <p className="text-xs text-text-secondary mt-1">Look for an email from LawDesk KE with the subject "Verify Your Email"</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center mt-0.5">
                <span className="text-xs font-bold text-primary">2</span>
              </div>
              <div>
                <p className="text-sm text-text-primary font-medium">Click the verification link</p>
                <p className="text-xs text-text-secondary mt-1">The link will activate your account and redirect you to the login page</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center mt-0.5">
                <span className="text-xs font-bold text-primary">3</span>
              </div>
              <div>
                <p className="text-sm text-text-primary font-medium">Sign in to your account</p>
                <p className="text-xs text-text-secondary mt-1">Once verified, you can access the LawDesk KE platform</p>
              </div>
            </div>
          </div>

          {/* Success Message */}
          {resendSuccess && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center space-x-2">
              <Icon name="CheckCircle" size={16} className="text-green-600 flex-shrink-0" />
              <span className="text-sm text-green-700">Verification email resent successfully!</span>
            </div>
          )}

          {/* Error Message */}
          {resendError && (
            <div className="bg-error-50 border border-error-200 rounded-lg p-3 flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} className="text-error-600 flex-shrink-0" />
              <span className="text-sm text-error-700">{resendError}</span>
            </div>
          )}

          {/* Proceed to Lawyer Matching (if came from client registration) */}
          {fromClientRegistration && (
            <div className="bg-secondary-50 border border-border rounded-lg p-4">
              <p className="text-sm text-text-secondary mb-3">
                <strong className="text-text-primary">Don't want to wait?</strong> You can browse lawyer matches now and verify your email later.
              </p>
              <button
                onClick={handleProceedToMatching}
                className="w-full bg-primary text-white py-2.5 px-4 rounded-lg font-medium hover:bg-primary-700 transition-smooth flex items-center justify-center space-x-2"
              >
                <Icon name="Search" size={16} />
                <span>Browse Lawyer Matches Now</span>
              </button>
            </div>
          )}

          {/* Resend Button */}
          <button
            onClick={handleResendVerification}
            disabled={isResending || countdown > 0}
            className="w-full border border-primary text-primary py-3 px-4 rounded-lg font-medium hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {isResending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent"></div>
                <span>Sending...</span>
              </>
            ) : countdown > 0 ? (
              <span>Resend in {countdown}s</span>
            ) : (
              <>
                <Icon name="RefreshCw" size={20} />
                <span>Resend Verification Email</span>
              </>
            )}
          </button>

          {/* Help Text */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <p className="text-xs font-medium text-text-primary flex items-center space-x-2">
              <Icon name="Info" size={14} className="text-text-secondary" />
              <span>Didn't receive the email?</span>
            </p>
            <ul className="text-xs text-text-secondary space-y-1 ml-5 list-disc">
              <li>Check your spam or junk folder</li>
              <li>Make sure you entered the correct email address</li>
              <li>Wait a few minutes and try resending</li>
              <li>Contact support if the issue persists</li>
            </ul>
          </div>

          {/* Back to Login */}
          <button
            onClick={() => navigate('/login')}
            className="w-full text-primary hover:text-primary-700 font-medium py-2 flex items-center justify-center space-x-2 transition-smooth"
          >
            <Icon name="ArrowLeft" size={16} />
            <span>Back to Login</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPending;